/**
 * The ONLY place app code may read density values (CLAUDE.md §12).
 *
 * Every value resolves to a design token — this module is a named, greppable
 * indirection, not a second source of truth. A raw pixel literal anywhere else
 * in `src/` is a bug and `npm run lint` fails on it.
 */
export const DENSITY = {
  rowHeight: 'var(--mh-density-row-height)',
  rowPaddingY: 'var(--mh-density-row-padding-y)',
  rowPaddingX: 'var(--mh-density-row-padding-x)',
  headerHeight: 'var(--mh-density-header-height)',
  controlHeight: 'var(--mh-density-control-height)',
  toolbarHeight: 'var(--mh-density-toolbar-height)',
  sidebarWidth: 'var(--mh-density-sidebar-width)',
  overlayWidth: 'var(--mh-density-overlay-width)',
  overlayWidthWide: 'var(--mh-density-overlay-wide)',
  thumb: 'var(--mh-density-thumb)',
  avatar: 'var(--mh-density-avatar)',
  hour: 'var(--mh-density-hour)',
  timeGutter: 'var(--mh-density-time-gutter)',
  drawerWidth: 'var(--mh-density-drawer-width)',
  /* How tall one open filter section may grow before it scrolls inside itself.
     «شهر» has 31 options and would otherwise push half the panel off-screen. */
  filterSectionMax: 'var(--mh-density-filter-section-max)',
};

/** Rows per page in dense tables. Not a pixel value, but a density decision. */
export const PAGE_SIZE = 25;
// How many contacts one request may fetch when «انتخاب همهٔ N مورد منطبق» has
// to become a real id list (unticking one row under it). Mirrors the CRM's
// `MAX_PAGE_SIZE` in domain/search_service.py; asking for more is clamped
// silently to this, so a larger number here would fetch the same page twice.
export const SELECTION_FETCH_PAGE_SIZE = 200;

/** Interactions loaded per timeline page. Smaller than a table page: each entry
 *  is several lines tall, so 25 would already be a long scroll. */
export const TIMELINE_PAGE_SIZE = 15;

/** How long the contacts search waits after the last keystroke, in ms.
 *  A density decision about network chatter rather than pixels: short enough to
 *  feel live, long enough that typing a full surname is one query, not eight. */
export const SEARCH_DEBOUNCE_MS = 250;

/** How long the send dialog waits after the last keystroke before asking the
 *  server to re-render the preview.
 *
 *  Longer than the search debounce, because the cost of being eager is worse:
 *  `previewSend` is `network-only`, so every re-query blanks `data` while it
 *  is in flight and the preview panel UNMOUNTS and remounts. Typing a sentence
 *  made it flash on every character. Holding the previous preview is what
 *  fixes the flicker; this is what stops the request storm underneath it. */
export const PREVIEW_DEBOUNCE_MS = 400;

/** Tailwind classes for the standard dense table cell, so screens agree. */
export const TABLE_CELL = 'px-4 py-2 align-middle';

/** The calendar's working day. Outside these hours the grid scrolls rather
 *  than disappearing — a 7am install and a 9pm call both happen. */
export const DAY_START_HOUR = 7;
export const DAY_END_HOUR = 21;

/** How finely a drag snaps, in minutes. Fifteen is small enough to place a
 *  meeting honestly and large enough that a shaky drag lands somewhere sane. */
export const CALENDAR_SNAP_MINUTES = 15;

/** Default length of an activity created by a click rather than a drag. */
export const DEFAULT_ACTIVITY_MINUTES = 60;

/** How often the client asks for fired reminders, in ms. A poll rather than a
 *  subscription: one query a minute is cheaper than holding a socket open for
 *  every open tab, and the tray is not a real-time surface. A subscription is
 *  roadmap (todo.md). */
export const REMINDER_POLL_MS = 60_000;

/**
 * The one breakpoint, and the only one there will be.
 *
 * MeshHub is desktop-first with exactly two mobile reductions (CLAUDE.md §12),
 * so there is no ladder of breakpoints to keep in step — there is a screen big
 * enough for the contact table and a screen that is not.
 *
 * 768 because that is where the table stops fitting without horizontal scroll.
 * Measured, not chosen for symmetry with anything.
 *
 * A MEDIA QUERY rather than a user-agent test: what matters is how much room
 * there is, not what the device claims to be. A phone in landscape and a
 * narrow window get the same answer, which is right for both.
 */
export const MOBILE_MAX_WIDTH = 767;
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
